import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { HandTile } from '../HandTile';
import { calculateTotalCombos, TOTAL_POSSIBLE_COMBOS } from './range-combos-calculator';

// ---- Physics / feel ----
const CELL_SIZE = 60;              // Grid cell size in px
const CELL_GAP = 2;                // Grid gap in px
const CONTAINER_PADDING = 16;      // Uniform padding inside wrapper on all sides
const RUBBER_SLOPE = 1.0;          // Initial 1:1 finger-to-matrix ratio past bounds (no clutch near edge)
const RUBBER_LIMIT = 90;           // Asymptotic max visible overshoot in px (you can keep pulling; it just tightens)
const SPRING_K = 0.18;             // Spring stiffness (force per unit displacement)
const SPRING_C = 0.95;             // Spring damping (slightly over-critical ≈ 2·√K = 0.85) → no overshoot
const INERTIA_FRICTION = 0.88;     // Velocity decay per frame inside bounds
const MIN_VELOCITY = 0.08;         // Stop threshold
const VELOCITY_CAP = 35;           // Max velocity in px/frame — lower = calmer glide

// Matrix displays cards in reverse order (from A to 2)
const CARD_VALUES = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];

const normalizeHandKey = (key) => {
  if (!key) return '';
  const handKey = String(key);
  if (/^([2-9TJQKA])\1p$/i.test(handKey)) return handKey.slice(0, 2);
  if (/^([2-9TJQKA])([2-9TJQKA])s+$/i.test(handKey)) return handKey.slice(0, 2) + 's';
  if (/^([2-9TJQKA])([2-9TJQKA])o+$/i.test(handKey)) return handKey.slice(0, 2) + 'o';
  return handKey;
};

// Simple subtype → exact CSV action key (for Open-type filters).
const FILTER_ACTION_MAP = {
  raise: 'raise',
  jam:   'allin',
  call:  'call',   // Open Type "Call" = hero limps/calls the BB
  limp:  'call',   // legacy alias
  // 'all', undefined, and BB-specific modes handled separately below
};

/**
 * Resolve which actions to display given a filterMode and the hand's actions object.
 * Returns { [actionKey]: frequency } to display, or null if the hand doesn't match.
 *
 * Returned keys are intentionally kept as specific raise_X strings (not collapsed
 * to 'raise') so HandTile can use globalRaiseSizes to assign the correct shade.
 *
 * globalRaiseSizes: sorted-ascending list of every raise_X key seen ANYWHERE in the
 * loaded range (same list HandTile uses to decide small vs. large shading). This is
 * required — NOT just the keys present on this one hand — because a hand whose only
 * nonzero raise action happens to be the large size would otherwise look, from its
 * own perspective, like "my only/smallest raise key", and incorrectly match a
 * Small Raise filter (and a small-only hand would likewise leak into Large Raise).
 *
 * BB-specific modes:
 *   'check'       → { call: freq }           green
 *   'raise_X'     → { raise_X: freq }        HandTile colors per globalRaiseSizes
 *   'allin'       → { allin: freq }           dark red
 *   'raise-small' → the globally-smallest raise_X key only
 *   'raise-large' → any raise_X key that isn't the globally-smallest
 *   'raise-all'   → all raise_X keys (HandTile colors each)
 */
const computeMatchedActions = (filterMode, actions, globalRaiseSizes = []) => {
  if (!filterMode) return null; // no filter — show everything

  // ── BB check filter ───────────────────────────────────────────────────────
  if (filterMode === 'check') {
    const freq = (actions.check ?? actions.call) || 0;
    return freq > 0 ? { call: freq } : null;
  }

  // ── Specific raise_X size (BB Option: e.g. raise_5, raise_8) ─────────────
  // Return the original key so HandTile knows the exact size for coloring.
  if (filterMode.startsWith('raise_')) {
    const freq = actions[filterMode] || 0;
    return freq > 0 ? { [filterMode]: freq } : null;
  }

  // ── Jam/allin filter (BB Option → Jam) ────────────────────────────────────
  if (filterMode === 'allin') {
    const freq = (actions.allin || actions.jam) || 0;
    return freq > 0 ? { allin: freq } : null;
  }

  // ── BB raise-size filters (raise-small / raise-large / raise-all) ─────────
  if (filterMode === 'raise-all' || filterMode === 'raise-small' || filterMode === 'raise-large') {
    const handRaiseKeys = Object.keys(actions).filter(k => /^raise_/.test(k));
    if (handRaiseKeys.length === 0) return null;

    if (filterMode === 'raise-all') {
      // Return individual keys so HandTile can shade each correctly
      const raiseActions = {};
      handRaiseKeys.forEach(k => { if (actions[k]) raiseActions[k] = actions[k]; });
      const total = Object.values(raiseActions).reduce((s, f) => s + f, 0);
      return total > 0 ? raiseActions : null;
    }

    // Judge "small" vs "large" against the GLOBAL raise-size list, not just the
    // keys this particular hand happens to have.
    const smallestGlobalKey = globalRaiseSizes[0];
    const matchingKeys = filterMode === 'raise-small'
      ? handRaiseKeys.filter(k => k === smallestGlobalKey)
      : handRaiseKeys.filter(k => k !== smallestGlobalKey);
    if (matchingKeys.length === 0) return null;

    const raiseActions = {};
    matchingKeys.forEach(k => { if (actions[k]) raiseActions[k] = actions[k]; });
    const total = Object.values(raiseActions).reduce((s, f) => s + f, 0);
    return total > 0 ? raiseActions : null;
  }

  // ── Standard Open-type filter ─────────────────────────────────────────────
  const targetAction = FILTER_ACTION_MAP[filterMode];
  if (!targetAction) return null;

  // 'raise' — return all raise_X keys so HandTile can shade each correctly
  if (targetAction === 'raise') {
    const raiseKeys = Object.keys(actions).filter(k => /^raise_/.test(k) || k === 'raise');
    if (raiseKeys.length === 0) return null;
    const raiseActions = {};
    raiseKeys.forEach(k => { if (actions[k]) raiseActions[k] = actions[k]; });
    const total = Object.values(raiseActions).reduce((s, f) => s + f, 0);
    return total > 0 ? raiseActions : null;
  }

  // 'allin' — also accept 'jam' as an alias
  if (targetAction === 'allin') {
    const freq = (actions.allin || actions.jam) || 0;
    return freq > 0 ? { allin: freq } : null;
  }

  // 'call' and 'limp' are the same action — CSVs may use 'call', 'limp', or 'check' (SB complete/limp)
  const freq = actions[targetAction] || (targetAction === 'call' ? (actions['limp'] || actions['check']) : 0) || 0;
  return freq > 0 ? { [targetAction]: freq } : null;
};

/**
 * Wraps computeMatchedActions to also surface how much of the hand's frequency,
 * beyond the action(s) matched by this filter, is still made up of OTHER real
 * actions from the loaded range (as opposed to true fold, which CSVs never
 * encode explicitly — a hand's total absence from a row is how 100% fold is
 * spelled). That leftover is tagged 'inRange' so HandTile can paint it in the
 * darker "still in range, just a different action" tan instead of the lighter
 * fold/out-of-range tan. Whatever's left after THAT is genuine fold.
 *
 * Note: this is specific to the reference range display (RangeScreen /
 * ViewRangeScreen / BlastRangeScreen / BlastViewRangeScreen). It's intentionally
 * NOT how BlastAnswerScreen grades a quiz answer, where any action other than
 * the one being tested reads as "Fold" on purpose — see the comments above
 * getBlastCorrectLabel() in BlastAnswerScreen.jsx.
 */
const resolveFilteredActions = (filterMode, actions, globalRaiseSizes = []) => {
  const matched = computeMatchedActions(filterMode, actions, globalRaiseSizes);
  if (!matched) return matched;

  const rawTotal = Object.values(actions || {}).reduce((s, f) => s + (f || 0), 0);
  const matchedTotal = Object.values(matched).reduce((s, f) => s + (f || 0), 0);
  const otherInRange = rawTotal - matchedTotal;

  if (otherInRange > 0.01) {
    return { ...matched, inRange: (matched.inRange || 0) + otherInRange };
  }
  return matched;
};

const RangeMatrix = ({ activeRange, filterMode, size = 'default' }) => {
  const [scale, setScale] = useState(1);
  const [handActivationMap, setHandActivationMap] = useState({});
  const [activeHandCount, setActiveHandCount] = useState(0);
  const [activeCombosCount, setActiveCombosCount] = useState(0);
  const [matrixCells, setMatrixCells] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // DOM refs
  const containerRef = useRef(null);
  const matrixRef = useRef(null);

  // Imperative state (avoids React re-renders during drag → smooth 60fps)
  const positionRef = useRef({ x: CONTAINER_PADDING, y: CONTAINER_PADDING });
  const velocityRef = useRef({ x: 0, y: 0 });
  const dragOriginRef = useRef({ x: 0, y: 0 });       // pointer - position at drag start
  const isDraggingRef = useRef(false);
  const lastSampleRef = useRef({ x: 0, y: 0, t: 0 }); // for velocity sampling
  const rafRef = useRef(null);
  const pinchDistRef = useRef(null);
  const scaleRef = useRef(1);
  const hasCenteredRef = useRef(false);

  // Keep scaleRef in sync with scale state
  useEffect(() => { scaleRef.current = scale; }, [scale]);

  // ---- Transform application (direct DOM write, bypasses React) ----
  const applyTransform = useCallback(() => {
    const el = matrixRef.current;
    if (!el) return;
    const { x, y } = positionRef.current;
    el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scaleRef.current})`;
  }, []);

  // ---- Boundary math with uniform padding on all sides ----
  const getBoundaries = useCallback(() => {
    const container = containerRef.current;
    if (!container) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const matrixSize = (CARD_VALUES.length * CELL_SIZE + (CARD_VALUES.length - 1) * CELL_GAP) * scaleRef.current;

    // If matrix fits in one axis, lock to centered. Otherwise allow pan with equal padding on both sides.
    let minX, maxX, minY, maxY;

    if (matrixSize + CONTAINER_PADDING * 2 <= cw) {
      const centerX = (cw - matrixSize) / 2;
      minX = maxX = centerX;
    } else {
      // matrix is wider: can pan between fully-left (showing right side) and fully-right (showing left side)
      minX = cw - CONTAINER_PADDING - matrixSize; // most-negative: right edge of matrix at right edge - padding
      maxX = CONTAINER_PADDING;                    // most-positive: left edge at padding
    }

    if (matrixSize + CONTAINER_PADDING * 2 <= ch) {
      const centerY = (ch - matrixSize) / 2;
      minY = maxY = centerY;
    } else {
      minY = ch - CONTAINER_PADDING - matrixSize;
      maxY = CONTAINER_PADDING;
    }

    return { minX, maxX, minY, maxY };
  }, []);

  const clampToBounds = useCallback((x, y) => {
    const { minX, maxX, minY, maxY } = getBoundaries();
    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y)),
    };
  }, [getBoundaries]);

  // Apply rubber-band resistance beyond bounds (iOS Scroll View-style asymptotic curve)
  // damped = (overshoot · LIMIT) / (overshoot + LIMIT / SLOPE)
  //   - initial slope (near boundary) = SLOPE (1.0 → 1:1, no instant clutch)
  //   - horizontal asymptote = LIMIT (no matter how hard you pull, visible overshoot ≤ LIMIT)
  // So the drag never feels capped; you just pull into ever-increasing resistance.
  const applyRubberBand = useCallback((x, y) => {
    const { minX, maxX, minY, maxY } = getBoundaries();
    const rubberize = (overshoot) =>
      (overshoot * RUBBER_LIMIT) / (overshoot + RUBBER_LIMIT / RUBBER_SLOPE);
    const rubber = (val, min, max) => {
      if (val < min) return min - rubberize(min - val);
      if (val > max) return max + rubberize(val - max);
      return val;
    };
    return { x: rubber(x, minX, maxX), y: rubber(y, minY, maxY) };
  }, [getBoundaries]);

  // ---- RAF loop: damped spring snap-back when out of bounds + inertia inside ----
  const runAnimation = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const step = () => {
      const pos = positionRef.current;
      const vel = velocityRef.current;
      const { minX, maxX, minY, maxY } = getBoundaries();

      // --- X axis ---
      if (pos.x < minX || pos.x > maxX) {
        // Damped spring: a = -K·x - C·v (Hooke + viscous damping)
        // With C slightly above 2·√K → critically damped → no overshoot past boundary.
        const targetX = pos.x < minX ? minX : maxX;
        const disp = pos.x - targetX;
        const accel = -SPRING_K * disp - SPRING_C * vel.x;
        vel.x += accel;
      } else {
        vel.x *= INERTIA_FRICTION;
      }

      // --- Y axis ---
      if (pos.y < minY || pos.y > maxY) {
        const targetY = pos.y < minY ? minY : maxY;
        const disp = pos.y - targetY;
        const accel = -SPRING_K * disp - SPRING_C * vel.y;
        vel.y += accel;
      } else {
        vel.y *= INERTIA_FRICTION;
      }

      let newX = pos.x + vel.x;
      let newY = pos.y + vel.y;

      // Stop conditions
      const outsideXAfter = newX < minX || newX > maxX;
      const outsideYAfter = newY < minY || newY > maxY;

      const axisSettled = (outside, n, lo, hi, v) => {
        if (outside) {
          const edge = n < lo ? lo : hi;
          return Math.abs(n - edge) < 0.5 && Math.abs(v) < MIN_VELOCITY;
        }
        return Math.abs(v) < MIN_VELOCITY;
      };

      const settledX = axisSettled(outsideXAfter, newX, minX, maxX, vel.x);
      const settledY = axisSettled(outsideYAfter, newY, minY, maxY, vel.y);

      if (settledX && settledY) {
        if (outsideXAfter) newX = newX < minX ? minX : maxX;
        if (outsideYAfter) newY = newY < minY ? minY : maxY;
        positionRef.current = { x: newX, y: newY };
        velocityRef.current = { x: 0, y: 0 };
        applyTransform();
        rafRef.current = null;
        return;
      }

      positionRef.current = { x: newX, y: newY };
      applyTransform();
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
  }, [getBoundaries, applyTransform]);

  const stopAnimation = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // ---- Pointer handlers (unified for touch + mouse) ----
  const onPointerDown = useCallback((clientX, clientY) => {
    stopAnimation();
    isDraggingRef.current = true;
    setIsDragging(true);
    dragOriginRef.current = {
      x: clientX - positionRef.current.x,
      y: clientY - positionRef.current.y,
    };
    // Track velocity from actual matrix position (not raw pointer), so rubber-band
    // resistance correctly attenuates the release velocity.
    lastSampleRef.current = {
      x: positionRef.current.x,
      y: positionRef.current.y,
      t: performance.now(),
    };
    velocityRef.current = { x: 0, y: 0 };
  }, [stopAnimation]);

  const onPointerMove = useCallback((clientX, clientY) => {
    if (!isDraggingRef.current) return;
    const rawX = clientX - dragOriginRef.current.x;
    const rawY = clientY - dragOriginRef.current.y;

    // Rubber-band when dragging past bounds
    const { x, y } = applyRubberBand(rawX, rawY);

    // Sample velocity from the rubber-banded position. If the matrix is being
    // held past the boundary, visible motion is small → release velocity is small.
    const now = performance.now();
    const dt = now - lastSampleRef.current.t;
    if (dt > 0) {
      const dx = x - lastSampleRef.current.x;
      const dy = y - lastSampleRef.current.y;
      // px per frame (normalized to ~16ms frame) — weighted average smooths jitter
      const weight = 0.35;
      const frameVelX = (dx / dt) * 16;
      const frameVelY = (dy / dt) * 16;
      velocityRef.current.x = velocityRef.current.x * (1 - weight) + frameVelX * weight;
      velocityRef.current.y = velocityRef.current.y * (1 - weight) + frameVelY * weight;
      lastSampleRef.current = { x, y, t: now };
    }

    positionRef.current = { x, y };
    applyTransform();
  }, [applyRubberBand, applyTransform]);

  const onPointerUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);

    // Cap velocity for safety
    const vx = Math.max(-VELOCITY_CAP, Math.min(VELOCITY_CAP, velocityRef.current.x));
    const vy = Math.max(-VELOCITY_CAP, Math.min(VELOCITY_CAP, velocityRef.current.y));
    velocityRef.current = { x: vx, y: vy };

    runAnimation();
  }, [runAnimation]);

  // Touch handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      onPointerDown(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      isDraggingRef.current = false;
      setIsDragging(false);
      pinchDistRef.current = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && isDraggingRef.current) {
      onPointerMove(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2 && pinchDistRef.current !== null) {
      const newDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const diff = newDist - pinchDistRef.current;
      const newScale = Math.min(Math.max(scaleRef.current + diff * 0.005, 0.5), 2);
      scaleRef.current = newScale;
      setScale(newScale);
      applyTransform();
      pinchDistRef.current = newDist;
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length === 0) {
      pinchDistRef.current = null;
      onPointerUp();
    } else if (e.touches.length === 1 && pinchDistRef.current !== null) {
      // Released one finger during pinch — resume single-finger drag with remaining finger
      pinchDistRef.current = null;
      onPointerDown(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    onPointerDown(e.clientX, e.clientY);

    // Attach window-level listeners so drag continues outside element
    const move = (ev) => onPointerMove(ev.clientX, ev.clientY);
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      onPointerUp();
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // ---- Process activeRange into handActivationMap ----
  useEffect(() => {
    const newHandMap = {};
    let count = 0;

    try {
      if (!activeRange) {
        setHandActivationMap({});
        setActiveHandCount(0);
        return;
      }

      const processEntry = (key, value) => {
        const normalizedKey = normalizeHandKey(key);
        let actions = {};

        if (typeof value === 'number') {
          // Old format: plain frequency number
          const freq = value > 100 && value <= 10000 ? Math.round(value / 100) : value;
          if (freq > 0) actions = { open: freq };
        } else if (value === true) {
          actions = { open: 100 };
        } else if (typeof value === 'object' && value !== null) {
          if (value.actions && Object.keys(value.actions).length > 0) {
            // New action-aware format from parseRangeFromCSV
            actions = value.actions;
          } else if (typeof value.frequency === 'number' && value.frequency > 0) {
            // Old object format { hand, type, frequency }
            const freq = value.frequency > 100 ? Math.round(value.frequency / 100) : value.frequency;
            actions = { open: freq };
          }
        }

        const totalFreq = Object.values(actions).reduce((sum, f) => sum + (f || 0), 0);
        if (totalFreq > 0) {
          newHandMap[normalizedKey] = { active: true, actions };
          count++;
        }
      };

      if (activeRange instanceof Map) {
        activeRange.forEach((value, key) => processEntry(key, value));
      } else if (Array.isArray(activeRange)) {
        activeRange.forEach(item => {
          if (!item) return;
          if (typeof item === 'string') {
            processEntry(item, 100);
          } else if (typeof item === 'object') {
            if (item.hand) {
              processEntry(item.hand, item.frequency || 0);
            } else {
              const entries = Object.entries(item);
              if (entries.length > 0) processEntry(entries[0][0], entries[0][1]);
            }
          }
        });
      } else if (typeof activeRange === 'object') {
        Object.entries(activeRange).forEach(([key, value]) => processEntry(key, value));
      }

      setActiveHandCount(count);
      setHandActivationMap(newHandMap);
    } catch (error) {
      console.error('Error processing activeRange:', error);
      setHandActivationMap({});
      setActiveHandCount(0);
    }
  }, [activeRange]);

  // ---- Compute global raise sizes from entire range ----
  // Sorted smallest-to-largest so HandTile can assign raise-small / raise-large colors.
  const globalRaiseSizes = useMemo(() => {
    const sizeSet = new Set();
    Object.values(handActivationMap).forEach(({ actions }) => {
      Object.keys(actions || {}).forEach(k => {
        if (/^raise_/.test(k)) sizeSet.add(k);
      });
    });
    return [...sizeSet].sort(
      (a, b) => (parseFloat(a.split('_')[1]) || 0) - (parseFloat(b.split('_')[1]) || 0)
    );
  }, [handActivationMap]);

  // ---- Build matrix cells ----
  const isHandActive = useCallback((handKey) => {
    const formats = [handKey, normalizeHandKey(handKey), handKey.substring(0, 2)];

    for (const fmt of formats) {
      const entry = handActivationMap[fmt];
      if (entry?.active && entry.actions) {
        const total = Object.values(entry.actions).reduce((s, f) => s + (f || 0), 0);
        if (total > 0) return { active: true, actions: entry.actions };
      }
    }

    // Fallback: check activeRange directly (handles legacy formats)
    if (activeRange instanceof Map) {
      for (const fmt of formats) {
        if (activeRange.has(fmt)) {
          const val = activeRange.get(fmt);
          if (typeof val === 'number' && val > 0) return { active: true, actions: { open: val } };
          if (val?.actions) return { active: true, actions: val.actions };
        }
      }
    }

    return { active: false, actions: {} };
  }, [handActivationMap, activeRange]);

  useEffect(() => {
    const applyFilter = (active, actions) => {
      // No filter, 'all', or inactive → show as-is
      if (!filterMode || filterMode === 'all' || !active) return { active, actions };
      const filtered = resolveFilteredActions(filterMode, actions, globalRaiseSizes);
      if (filtered) return { active: true, actions: filtered };
      // Hand is in the range file but not for the selected action/filter.
      // Show as "in range" (tan) so the full range shape stays visible.
      return { active: true, actions: { inRange: 100 } };
    };

    const matrix = [];
    for (let i = 0; i < CARD_VALUES.length; i++) {
      for (let j = 0; j < CARD_VALUES.length; j++) {
        const rank1 = CARD_VALUES[i];
        const rank2 = CARD_VALUES[j];

        if (i === j) {
          const handText = `${rank1}${rank1}`;
          const raw = isHandActive(handText);
          const { active, actions } = applyFilter(raw.active, raw.actions);
          matrix.push({
            key: `${i}-${j}`, text: handText, displayText: handText,
            type: 'pair', isActive: active, actions,
          });
        } else {
          const [higherRank, lowerRank] = CARD_VALUES.indexOf(rank1) <= CARD_VALUES.indexOf(rank2)
            ? [rank1, rank2] : [rank2, rank1];
          const isSuited = i < j;
          const handText = `${higherRank}${lowerRank}${isSuited ? 's' : 'o'}`;
          const raw = isHandActive(handText);
          const { active, actions } = applyFilter(raw.active, raw.actions);
          matrix.push({
            key: `${i}-${j}`, text: `${higherRank}${lowerRank}`, displayText: handText,
            type: isSuited ? 'suited' : 'offsuit', isActive: active, actions,
          });
        }
      }
    }
    setMatrixCells(matrix);
    setActiveCombosCount(calculateTotalCombos(matrix));
  }, [handActivationMap, isHandActive, filterMode, globalRaiseSizes]);

  // ---- Initial positioning: show top-left (AA corner) with uniform padding ----
  // useLayoutEffect so the transform is applied before first paint (no flash).
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    if (hasCenteredRef.current) return;

    const { maxX, maxY } = getBoundaries();
    // maxX/maxY === CONTAINER_PADDING when matrix is bigger than container,
    // or === centered value when it fits. Either way gives equal margins.
    positionRef.current = { x: maxX, y: maxY };
    applyTransform();
    hasCenteredRef.current = true;
  }, [getBoundaries, applyTransform]);

  // Clamp position when scale changes
  useEffect(() => {
    if (!hasCenteredRef.current) return;
    const clamped = clampToBounds(positionRef.current.x, positionRef.current.y);
    positionRef.current = clamped;
    applyTransform();
  }, [scale, clampToBounds, applyTransform]);

  // Recenter on window resize
  useEffect(() => {
    const onResize = () => {
      const clamped = clampToBounds(positionRef.current.x, positionRef.current.y);
      positionRef.current = clamped;
      applyTransform();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampToBounds, applyTransform]);

  // Cleanup RAF on unmount
  useEffect(() => () => stopAnimation(), [stopAnimation]);

  return (
    <div
      ref={containerRef}
      className={`range-matrix-wrapper ${size}`}
      style={{
        width: '100%',
        height: size === 'large' ? '560px' : size === 'fill' ? '100%' : '450px',
        backgroundColor: '#F2EFE9',
        borderRadius: '16px',
        border: '1px solid #E4E1DF',
        overflow: 'hidden',
        position: 'relative',
        touchAction: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        ref={matrixRef}
        className="matrix-content"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${CARD_VALUES.length}, ${CELL_SIZE}px)`,
          gridTemplateRows: `repeat(${CARD_VALUES.length}, ${CELL_SIZE}px)`,
          gap: `${CELL_GAP}px`,
          position: 'absolute',
          top: 0,
          left: 0,
          margin: 0,
          transformOrigin: '0 0',
          cursor: isDragging ? 'grabbing' : 'grab',
          willChange: 'transform',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          touchAction: 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        {matrixCells.map(cell => (
          <HandTile
            key={cell.key}
            text={cell.text}
            type={cell.type}
            isActive={cell.isActive}
            actions={cell.actions}
            globalRaiseSizes={globalRaiseSizes}
          />
        ))}
      </div>

      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        background: 'rgba(0,0,0,0.8)',
        padding: '3px 8px',
        borderRadius: '16px',
        fontSize: '12px',
        fontFamily: '"Poppins", Helvetica',
        fontWeight: 600,
        color: 'white',
        zIndex: 10,
        pointerEvents: 'none',
      }}>
        {Math.round((activeCombosCount / TOTAL_POSSIBLE_COMBOS) * 100)}% of hands
      </div>
    </div>
  );
};

export default RangeMatrix;
